import { useAtom } from 'jotai'
import { Container } from 'react-bootstrap'
import { useMediaQuery } from 'react-responsive'
import {
  matchPath,
  Redirect,
  Route,
  Switch,
  useLocation,
  useRouteMatch,
} from 'react-router-dom'
import { bookIdAtom } from 'store/atoms'
import { useBookRoleQuery } from 'store/queries'
import { breakpoints } from 'style'
import BooksList from './components/BooksList'
import DashboardHelp from './components/DashboardHelp'
import DashboardNav from './components/DashboardNav'
import DashboardRedirect from './components/DashboardRedirect'
import FinalPreview from './components/FinalPreview'
import AddFriends from './features/AddFriends'
import CoverEditor from './features/CoverEditor'
import BookInfo from './features/BookInfo'
import BookPreview from './features/BookPreview'
import BookSettings from './features/BookSettings'
import CoverTemplates from './features/CoverTemplates'
import Friends from './features/Friends'
import PendingInvites from './features/PendingInvites'
import PromptCatalog from './features/PromptCatalog'
import PromptFreq from './features/PromptFreq'
import PromptOrder from './features/PromptOrder'
import PublishTasks from './features/PublishTasks'
import RecentActivity from './features/RecentActivity'
import SetupGuide from './features/SetupGuide'
import Story from './features/Story'
import ThingsToDo from './features/ThingsToDo'
import { DashboardContainer } from './style'

function InfoRoutes() {
  const { path, url } = useRouteMatch()

  return (
    <Switch>
      <Route exact path={`${path}/`}>
        <Redirect to={`${url}/overview`} />
      </Route>
      <Route path={`${path}/overview`} component={BookInfo} />
      <Route path={`${path}/todo`} component={ThingsToDo} />
      <Route path={`${path}/settings`} component={BookSettings} />
      <Route path={`${path}/preview`} component={BookPreview} />
      <Route path={`${path}/activity`} component={RecentActivity} />
      {/* TODO: role guard */}
      <Route path={`${path}/setup`} component={SetupGuide} />
    </Switch>
  )
}

function FriendsRoutes() {
  const { path, url } = useRouteMatch()

  return (
    <Switch>
      <Route exact path={`${path}/`}>
        <Redirect to={`${url}/view`} />
      </Route>
      <Route path={`${path}/view`} component={Friends} />
      <Route path={`${path}/invites`} component={PendingInvites} />
      <Route path={`${path}/add`} component={AddFriends} />
    </Switch>
  )
}

function StoryRoutes({ role }) {
  const { path, url } = useRouteMatch()
  let defaultView
  if (role === 'Purchaser' || role === 'Editor') {
    defaultView = 'all'
  } else if (role === 'Author') {
    defaultView = 'mine'
  } else {
    defaultView = 'shared'
  }

  return (
    <Switch>
      <Route exact path={`${path}/`}>
        <Redirect to={`${url}/view/${defaultView}`} />
      </Route>
      <Route exact path={`${path}/view/:filter`} component={Story} />
      <Route path={`${path}/prompt-catalog`} component={PromptCatalog} />
      <Route path={`${path}/order`} component={PromptOrder} />
      <Route path={`${path}/frequency`} component={PromptFreq} />
    </Switch>
  )
}

function PublishRoutes() {
  const { path, url } = useRouteMatch()

  return (
    <Switch>
      <Route exact path={`${path}/`}>
        <Redirect to={`${url}/overview`} />
      </Route>
      <Route path={`${path}/overview`} component={PublishTasks} />
      <Route path={`${path}/cover-design/:coverId?`} component={CoverEditor} />
      <Route path={`${path}/cover-templates`} component={CoverTemplates} />
      <Route path={`${path}/preview`} component={FinalPreview} />
    </Switch>
  )
}

function Dashboard() {
  const { path, url } = useRouteMatch()
  const { pathname } = useLocation()
  const isMobile = useMediaQuery({
    query: `(max-width: ${breakpoints.sm}px)`,
  })

  const [bookId, setBookId] = useAtom(bookIdAtom)

  // This faffing is neccessary as react router route params are only available in
  // children of the route where they are defined.
  const match = matchPath(pathname, {
    path: `${url}/book/:bookId`,
  })

  const { data: role } = useBookRoleQuery(match?.params.bookId)

  if (match && match.params.bookId !== bookId) {
    setBookId(match.params.bookId)
  }

  return (
    <DashboardContainer isMobile={isMobile}>
      <Container fluid className="h-100">
        <div className="dashboard-wrapper">
          <div className="dashboard-header px-3 my-3">
            <BooksList />

            <DashboardHelp />
          </div>

          <div className="dashboard-body">
            <DashboardNav />

            <div className="dashboard-content">
              <Switch>
                <Route path={`${path}/book/:bookId/info`}>
                  <InfoRoutes />
                </Route>

                <Route path={`${path}/book/:bookId/friends`}>
                  <FriendsRoutes />
                </Route>

                <Route path={`${path}/book/:bookId/stories`}>
                  <StoryRoutes role={role?.name} />
                </Route>

                <Route path={`${path}/book/:bookId/publish`}>
                  <PublishRoutes />
                </Route>

                <Route path={path} component={DashboardRedirect} />
              </Switch>
            </div>
          </div>
        </div>
      </Container>
    </DashboardContainer>
  )
}

export default Dashboard
